import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useLiveAlertPreferences } from 'app/hooks/useLiveAlertPreferences'
import { useMessageEvents, type MessageEvent } from 'app/hooks/useMessageEvents'
import { playChime } from 'app/lib/alertChime'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { useAuthStore } from 'app/stores/StoreContext'
import type { ContactJson, LocationJson } from 'app/types/entities'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

/**
 * A front desk leaves this tab open all day, so the dedupe set is bounded.
 */
const MAX_SEEN_IDS = 200

/**
 * Customer-side messages only: `sentByUserId` and `isAgent` are left unset by the
 * inbound handler. The workspace clause is a cross-tab guard — see the hook body.
 */
export type MessageAlert = {
  id: string
  threadId: string
  locationId: string
  contactId?: string
  phone: string
  text: string
  createdDate: string
}

export type UseNewMessageAlertsReturn = {
  alerts: MessageAlert[]
  contactNames: Record<string, string>
  locationNames: Record<string, string>
  dismiss: (id: string) => void
}

/**
 * Turns one inbound Message event into an alert, applying the suppression rules that
 * belong here rather than in the view. Returns undefined when the message should raise
 * no card. Pure, so the rules are testable without a client.
 *
 * Rules:
 * 1. Only `created` inbound customer messages — not updates, not our own outbound, not AI.
 * 2. Only this tab's workspace. The httpOnly session cookie is shared across tabs, so a
 *    sibling tab on another workspace can move the ambient scope; the event carries its
 *    own workspaceId, so comparing it to the tab's own fails closed against a card for a
 *    workspace this tab is not showing.
 * 3. Not the thread the user is currently reading.
 */
export function messageEventToAlert({
  event,
  workspaceId,
  openThreadId,
}: {
  event: MessageEvent
  /** The workspace *this tab* is showing. */
  workspaceId: string
  openThreadId: string
}): MessageAlert | undefined {
  if (event.action !== `created`) return undefined
  const message = event.message
  if (message.sentByUserId || message.isAgent || message.archived) return undefined
  if (!message.id || !message.createdDate) return undefined
  if (workspaceId && message.workspaceId !== workspaceId) return undefined
  if (!message.threadId || !message.locationId) return undefined
  if (openThreadId && message.threadId === openThreadId) return undefined

  return {
    id: message.id,
    threadId: message.threadId,
    locationId: message.locationId,
    contactId: message.contactId ?? undefined,
    phone: message.from ?? ``,
    text: (message.text ?? ``).replace(/\s+/g, ` `).trim(),
    createdDate: String(message.createdDate),
  }
}

/**
 * Emits transient alerts for inbound texts arriving in any location the user can
 * access, for `<MessageAlertStack>`, driven by the live event stream.
 *
 * Two suppression rules live in `messageEventToAlert` so the component always
 * receives an already-correct list: nothing fires for our own/AI/updated messages,
 * and nothing fires for the thread the user is currently reading. Messages that
 * existed before mount never fire because the stream only delivers events that
 * happen while the socket is open — there is no backlog to seed past.
 */
export function useNewMessageAlerts(capacity: number): UseNewMessageAlertsReturn {
  const user = useUser()
  const workspace = useWorkspace()
  const { products } = useAuthStore()
  const routerLocation = useLocation()
  const [searchParams] = useSearchParams()

  // The workspace this tab is showing, captured from `me` at mount. Deliberately the
  // tab's own React state rather than anything ambient: it is the only value that stays
  // put when another tab moves the shared session cookie.
  const workspaceId = user?.workspaceId ?? ``

  const liveAlerts = useLiveAlertPreferences()

  // Same gate the sidebar unread badge uses, plus the user's own switch. An org
  // without Inbox should never be alerted about an inbox it does not have, and
  // someone who turned text alerts off should not have the poll running at all.
  // `loaded` keeps it quiet until the preference is known: on is the default, so
  // an unresolved setting is indistinguishable from on, and a card that appears
  // for half a second reads as the switch being broken.
  const enabled = !!(products?.includes(`Inbox`) && workspaceId && liveAlerts.loaded && liveAlerts.sms.popup)

  const [alerts, setAlerts] = useState<MessageAlert[]>([])
  const [contactNames, setContactNames] = useState<Record<string, string>>({})
  const [locationNames, setLocationNames] = useState<Record<string, string>>({})

  const seenIdsRef = useRef<string[]>([])
  const seenSetRef = useRef<Set<string>>(new Set<string>())
  const audioRef = useRef<HTMLAudioElement | undefined>(undefined)
  const capacityRef = useRef(capacity)
  const openThreadRef = useRef(``)
  const contactNamesRef = useRef<Record<string, string>>({})
  // A ref, because the event handler outlives a change to the sound switch: the
  // subscription is keyed on `enabled`, so muting mid-session must not tear it down.
  const soundRef = useRef(liveAlerts.sms.sound)

  useEffect(() => {
    capacityRef.current = capacity
  }, [capacity])

  useEffect(() => {
    soundRef.current = liveAlerts.sms.sound
  }, [liveAlerts.sms.sound])

  useEffect(() => {
    contactNamesRef.current = contactNames
  }, [contactNames])

  // The thread the user is currently reading, if any. Kept in a ref so the event
  // handler reads the live value without being torn down on every navigation.
  const openThreadId = routerLocation.pathname.startsWith(`/inbox`) ? (searchParams.get(`tab`) ?? ``) : ``

  useEffect(() => {
    openThreadRef.current = openThreadId
    if (!openThreadId) return
    // Navigating into a thread retires its card. Same principle as the
    // suppression rule: never alert someone about what they are already reading.
    setAlerts((prev) => prev.filter((alert) => alert.threadId !== openThreadId))
  }, [openThreadId])

  const dismiss = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id))
  }, [])

  useEffect(() => {
    if (!enabled) return
    bg(async () => {
      try {
        const { hits } = await gqlFetchList<LocationJson>(`locations`, { pagination: { limit: 200 } }, `id name`, {
          requestPolicy: `network-only`,
        })
        const next: Record<string, string> = {}
        for (const hit of hits) if (hit.id && hit.name) next[hit.id] = hit.name
        setLocationNames(next)
      } catch (err) {
        // A missing location label degrades the card, it does not break it.
        logError(`Error fetching location names for message alerts`, err)
      }
    })
  }, [enabled, workspace?.id])

  const markSeen = useCallback((id: string) => {
    if (seenSetRef.current.has(id)) return
    seenSetRef.current.add(id)
    seenIdsRef.current.push(id)
    const overflow = seenIdsRef.current.length - MAX_SEEN_IDS
    if (overflow <= 0) return
    for (const stale of seenIdsRef.current.splice(0, overflow)) seenSetRef.current.delete(stale)
  }, [])

  const resolveContactNames = useCallback(async (items: MessageAlert[]) => {
    const ids = Array.from(new Set(items.map((item) => item.contactId).filter((id): id is string => !!id))).filter(
      (id) => !contactNamesRef.current[id],
    )
    if (!ids.length) return
    const { hits } = await gqlFetchList<ContactJson>(
      `contacts`,
      { search: `id:(${ids.join(` OR `)})`, pagination: { limit: ids.length } },
      `id name preferredName`,
    )
    const next: Record<string, string> = {}
    for (const hit of hits) {
      const name = hit.preferredName || hit.name
      if (hit.id && name) next[hit.id] = name
    }
    if (Object.keys(next).length) setContactNames((prev) => ({ ...prev, ...next }))
  }, [])

  // Switching workspace or disabling clears the stack and the dedupe set so the
  // user never sees a card about the workspace they just left.
  useEffect(() => {
    setAlerts([])
    seenIdsRef.current = []
    seenSetRef.current = new Set<string>()
  }, [enabled, workspaceId, workspace?.id])

  // One inbound text, one card. The stream only delivers events that happen while the
  // socket is open, so there is no backlog to seed past: anything that arrives is new.
  useMessageEvents((event) => {
    const alert = messageEventToAlert({ event, workspaceId, openThreadId: openThreadRef.current })
    if (!alert || seenSetRef.current.has(alert.id)) return
    markSeen(alert.id)

    setAlerts((prev) => [alert, ...prev].slice(0, Math.max(capacityRef.current, 1)))
    if (soundRef.current) playChime(audioRef, [alert.id])
    bg(
      resolveContactNames([alert]).catch((err: unknown) =>
        // The card already renders the phone number; a failed lookup just means it stays that way.
        logError(`Error resolving contact names for message alerts`, err),
      ),
    )
  }, enabled)

  return { alerts, contactNames, locationNames, dismiss }
}
